import apiClient from "./apiClient";
import { isAuthenticated } from "./auth";

const CACHE_DURATION = 3 * 60 * 1000; // 3 minutos en milisegundos

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp);
  return (now - lastUpdate) < CACHE_DURATION;
};

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  // También invalidar caché de admin
  localStorage.removeItem('admin_products');
  localStorage.removeItem('admin_productsTimestamp');
};


// Nueva función para obtener productos con paginación
export const getProductsPaginated = async (page = 1, pageSize = 9, filters = {}) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Construir URL con parámetros de paginación y filtros
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Agregar filtros si existen
    if (filters.type && filters.type.length > 0) {
      url += `&type=${filters.type.join(',')}`;
    }
    if (filters.brands && filters.brands.length > 0) {
      url += `&brand=${filters.brands.join(',')}`;
      console.log('Adding brand filter to URL:', filters.brands);
    }
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.onlyDiscounted) {
      url += `&discount=true`;
    }
    if (filters.priceRange && filters.priceRange.length > 0) {
      url += `&priceRange=${filters.priceRange.join(',')}`;
    }
    if (filters.stockFilter && filters.stockFilter.length > 0) {
      url += `&stockFilter=${filters.stockFilter.join(',')}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    if (filters.ordering) {
      url += `&ordering=${filters.ordering}`;
    }
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    console.log('Fetching products with URL:', url);
    console.log('Filters applied:', filters);
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching paginated products:', error);
    throw error;
  }
};

export const getProducts = async (ignoreCache = false, page = 1, pageSize = 20) => {
  try {
    const cachedData = localStorage.getItem('products');
    const cacheTimestamp = localStorage.getItem('productsTimestamp');
    const isAuth = isAuthenticated();
    
    // Solo usar caché para la primera página
    if (!ignoreCache && page === 1 && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de productos');
      let products = JSON.parse(cachedData);
      
      // Filtrar productos no disponibles para usuarios no autenticados
      if (!isAuth) {
        products = products.filter(product => product.available !== false);
      }
      
      // Si el usuario no está autenticado, eliminar precios de los blisters
      if (!isAuth) {
        products = products.map(product => ({
          ...product,
          blisters: (product.blisters || []).map(blister => {
            const rest = { ...blister };
            delete rest.price;
            delete rest.discount;
            delete rest.final_price;
            return rest;
          })
        }));
      }
      return { results: products, count: products.length, next: null, previous: null };
    }

    console.log(`Obteniendo productos página ${page}`);
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    // Solo guardar en caché la primera página
    if (page === 1) {
      localStorage.setItem('products', JSON.stringify(result.results));
      localStorage.setItem('productsTimestamp', Date.now().toString());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función específica para admin que devuelve productos paginados sin cargar todo
export const getAllProductsForAdmin = async (ignoreCache = false, page = 1, pageSize = 20, filters = {}) => {
  try {
    const cachedData = localStorage.getItem('admin_products');
    const cacheTimestamp = localStorage.getItem('admin_productsTimestamp');
    
    // Solo usar caché para la primera página y sin filtros
    const hasFilters = Object.values(filters).some(value => value && value.length > 0);
    if (!ignoreCache && page === 1 && !hasFilters && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de productos admin');
      const products = JSON.parse(cachedData);
      return { results: products, count: products.length, next: null, previous: null };
    }

    console.log(`Obteniendo productos página ${page} para admin con filtros:`, filters);
    const token = localStorage.getItem("accessToken");
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Agregar filtros a la URL
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.brand) {
      url += `&brand=${encodeURIComponent(filters.brand)}`;
    }
    if (filters.type) {
      url += `&type=${encodeURIComponent(filters.type)}`;
    }
    if (filters.availability) {
      if (filters.availability === 'available') {
        url += `&available=true`;
      } else if (filters.availability === 'unavailable') {
        url += `&available=false`;
      }
    }
    if (filters.stockFilter) {
      url += `&stockFilter=${encodeURIComponent(filters.stockFilter)}`;
    }
    if (filters.priceStatus) {
      url += `&priceStatus=${encodeURIComponent(filters.priceStatus)}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    console.log(`Productos obtenidos para admin página ${page}: ${result.results.length}`);
    
    // Solo guardar en caché la primera página sin filtros
    if (page === 1 && !hasFilters) {
      localStorage.setItem('admin_products', JSON.stringify(result.results));
      localStorage.setItem('admin_productsTimestamp', Date.now().toString());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products for admin:', error);
    throw error;
  }
};

// Función para subir imagen a Cloudinary
export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('upload_preset', 'semillas');
    formData.append('cloud_name', 'dkpmp8fwq');
    formData.append('file', file);

    const response = await fetch(
      'https://api.cloudinary.com/v1_1/dkpmp8fwq/image/upload',
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    throw new Error('Error al subir la imagen: ' + (error.message || 'Error desconocido'));
  }
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/');
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Nueva función para obtener productos más recientes con stock y precio
export const getLatestProductsWithStock = async (limit = 8) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Endpoint para obtener productos más recientes con stock y precio
    let url = `/products/?ordering=-created_at&has_stock=true&has_price=true&page_size=${limit}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching latest products with stock:', error);
    return { results: [] };
  }
};

export const getProduct = async (id) => {
  try {
    console.log('Getting product with ID:', id);
    
    // Intentar obtener del caché primero
    const cachedProducts = localStorage.getItem('products');
    if (cachedProducts) {
      try {
        const products = JSON.parse(cachedProducts);
        if (Array.isArray(products)) {
          const cachedProduct = products.find(p => p.id === parseInt(id));
          if (cachedProduct) {
            console.log('Product found in cache:', cachedProduct);
            return cachedProduct;
          }
        }
      } catch (cacheError) {
        console.warn('Error parsing cached products:', cacheError);
      }
    }

    // Si no está en caché, hacer petición a la API
    console.log('Product not in cache, fetching from API...');
    
    // Incluir token de autenticación si el usuario está autenticado
    const headers = {};
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/${id}/`, { headers });
    console.log('Product API response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error in getProduct:', error);
    console.error('Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al obtener el producto');
  }
};

// Función para crear un nuevo producto
export const createProduct = async (productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Creando producto:', productData);
    const response = await apiClient.post("/products/", productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al crear el producto');
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Actualizando producto:', productId, productData);
    const response = await apiClient.put(`/products/${productId}/`, productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al actualizar el producto');
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al eliminar el producto');
  }
};

export const getBlisterStock = async (blisterId) => {
  try {
    const response = await apiClient.get(`/products/blisters/${blisterId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blister stock:', error);
    throw error;
  }
};

// Función para cambiar el estado de disponibilidad de un producto (solo admin)
export const toggleProductAvailability = async (productId, available) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando disponibilidad del producto:', productId, 'a:', available);
    const response = await apiClient.patch(`/products/${productId}/`, { available }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al cambiar la disponibilidad del producto');
  }
};
